import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import type { AuthSessionResponse } from '@brewcore/app-contract';
import { reasonText, t, type AuthClient, type AuthResult, type Lang } from '../core/index.js';
import {
  Banner,
  BrandMark,
  Button,
  CodeCells,
  codeGroups,
  GoogleLogo,
  IconButton,
  TextField,
} from './components/index.js';

type Step = { kind: 'email' } | { kind: 'code'; email: string; codeLength: number };

function errorText(lang: Lang, r: Extract<AuthResult<unknown>, { ok: false }>): string {
  if (r.error === 'network') return t(lang, 'networkError');
  if (r.error === 'limit' && r.retryAfter) return t(lang, 'retryIn', { n: r.retryAfter });
  const base = reasonText(lang, r.error);
  const text = base === r.error ? t(lang, 'loginFailed') : base;
  if (r.attemptsLeft === undefined || r.attemptsLeft <= 0) return text;
  // reason texts end with a full stop ("Der Code stimmt nicht."); any other text gets one before the second sentence
  return `${/[.!?]$/.test(text) ? text : `${text}.`} ${t(lang, 'attemptsLeft', { n: r.attemptsLeft })}`;
}

const digits = (raw: string) => raw.replace(/\D/g, '');

/**
 * Login (AD-32, FR-2) in two steps: e-mail address -> code from the mail (finished by POST in this same browser,
 * works inside the homescreen app without leaving it), or Google. Passkeys (operator) are offered by the browser's
 * autofill on the e-mail field (conditional mediation); the explicit passkey button stays as a quiet fallback.
 */
export function LoginScreen({
  auth,
  lang,
  initialError,
  onSignedIn,
  onToggleLang,
}: {
  auth: AuthClient;
  lang: Lang;
  initialError: string | null;
  onSignedIn: (s: AuthSessionResponse) => void;
  onToggleLang: () => void;
}) {
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  // the Google button only where the deployment has Google configured (unknown or failed: hidden)
  const [google, setGoogle] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);
  const autofillStarted = useRef(false);
  const onSignedInRef = useRef(onSignedIn);
  useEffect(() => {
    onSignedInRef.current = onSignedIn;
  }, [onSignedIn]);

  useEffect(() => {
    if (step.kind === 'code') codeRef.current?.focus();
  }, [step]);

  useEffect(() => {
    let alive = true;
    void auth.providers().then((r) => {
      if (alive) setGoogle(r.ok && r.value.google);
    });
    return () => {
      alive = false;
    };
  }, [auth]);

  // a pending passkey autofill request ends with the login screen
  useEffect(
    () => () => {
      if (autofillStarted.current) WebAuthnAbortService.cancelCeremony();
    },
    [],
  );

  const finishPasskey = async (response: unknown) => {
    const done = await auth.passkeyLogin(response);
    if (!done.ok) throw new Error(done.error);
    const s = await auth.session();
    if (s.ok && s.value) onSignedInRef.current(s.value);
    else throw new Error('session');
  };

  /** Passkey in the autofill of the e-mail field: started once, when the field is first focused. */
  const startAutofill = async () => {
    if (autofillStarted.current) return;
    autofillStarted.current = true;
    let chosen = false;
    try {
      if (!(await browserSupportsWebAuthnAutofill())) return;
      const options = await auth.passkeyLoginOptions();
      if (!options.ok) return;
      const response = await startAuthentication({
        optionsJSON: options.value as never,
        useBrowserAutofill: true,
      });
      chosen = true;
      setBusy(true);
      setError(null);
      await finishPasskey(response);
    } catch (err) {
      // cancelled (the passkey button, screen left) before a passkey was chosen: nothing to report
      if (chosen) setError(errorText(lang, { ok: false, error: (err as Error).message }));
    } finally {
      if (chosen) setBusy(false);
    }
  };

  const sendCode = async (address: string) => {
    setBusy(true);
    setError(null);
    const r = await auth.startEmail(address, lang);
    setBusy(false);
    if (!r.ok) return setError(errorText(lang, r));
    setCode('');
    setStep({ kind: 'code', email: address, codeLength: r.value.codeLength });
  };

  const onEmail = (e: FormEvent) => {
    e.preventDefault();
    void sendCode(email);
  };

  const onCode = async (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'code') return;
    setBusy(true);
    setError(null);
    const r = await auth.verifyEmail(step.email, code);
    setBusy(false);
    if (!r.ok) return setError(errorText(lang, r));
    onSignedIn(r.value);
  };

  const onGoogle = async () => {
    setBusy(true);
    setError(null);
    const r = await auth.startGoogle(location.pathname === '/login' ? '/' : location.pathname);
    if (!r.ok) {
      setBusy(false);
      return setError(errorText(lang, r));
    }
    location.assign(r.value.url); // same window: the homescreen app comes back to the callback
  };

  const onPasskey = async () => {
    setBusy(true);
    setError(null);
    try {
      const options = await auth.passkeyLoginOptions();
      if (!options.ok) throw new Error(options.error);
      const response = await startAuthentication({ optionsJSON: options.value as never });
      await finishPasskey(response);
    } catch (err) {
      setError(errorText(lang, { ok: false, error: (err as Error).message }));
    } finally {
      setBusy(false);
    }
  };

  const errorBanner = error && (
    <Banner tone="danger" icon="alert" role="alert" data-testid="login-error">
      {error}
    </Banner>
  );

  return (
    <main className="screen login" data-testid="login">
      <div className="login-top">
        <button type="button" className="link-btn dim" onClick={onToggleLang} data-testid="lang">
          {t(lang, 'language')}
        </button>
      </div>

      {step.kind === 'email' ? (
        <>
          {/* board "Anmelden": the small ring over the word, not the word inside a big ring */}
          <div className="login-hero">
            <BrandMark className="login-logo" />
            <span className="login-word">brewcore</span>
          </div>
          <div className="login-intro">
            <h1 className="title-xl">{t(lang, 'loginHeadline')}</h1>
            <p className="text-dim">{t(lang, 'loginIntro')}</p>
          </div>
          <form onSubmit={onEmail} className="stack">
            <TextField
              id="login-email"
              data-testid="login-email"
              label={t(lang, 'emailLabel')}
              type="email"
              autoComplete="username webauthn"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => void startAutofill()}
            />
            <Button
              type="submit"
              block
              busy={busy}
              disabled={busy || !email.includes('@')}
              data-testid="login-send"
            >
              {t(lang, 'sendCode')}
            </Button>
          </form>
          {errorBanner}
          <p className="divider">
            <span>{t(lang, 'or')}</span>
          </p>
          <div className="stack">
            {google && (
              <Button
                variant="secondary"
                block
                onClick={() => void onGoogle()}
                disabled={busy}
                data-testid="login-google"
              >
                <GoogleLogo />
                {t(lang, 'google')}
              </Button>
            )}
            {/* a second way in like Google, the same button (board "Anmelden") */}
            <Button
              variant="secondary"
              block
              icon="passkey"
              onClick={() => void onPasskey()}
              disabled={busy}
              data-testid="login-passkey"
            >
              {t(lang, 'passkeyLogin')}
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={onCode} className="login-code stack">
          <div className="login-intro">
            {/* board "Anmelden (Code)": back to the address at the left of the title */}
            <div className="login-code-head">
              <IconButton
                icon="chevronLeft"
                label={t(lang, 'otherAddress')}
                onClick={() => setStep({ kind: 'email' })}
                disabled={busy}
                data-testid="login-back"
              />
              <h1 className="title-xl">{t(lang, 'codeTitle')}</h1>
            </div>
            <p className="text-dim" data-testid="login-sent">
              {t(lang, 'codeSent', { email: step.email, n: step.codeLength })}
            </p>
          </div>
          <CodeCells
            id="login-code"
            ref={codeRef}
            data-testid="login-code"
            label={t(lang, 'codeLabel')}
            groups={codeGroups(step.codeLength)}
            significant={digits}
            value={code}
            onChange={setCode}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 \-]*"
            maxLength={step.codeLength + 2}
            required
            info={t(lang, 'noMailInfo')}
            infoLabel={t(lang, 'infoShow')}
          />
          <Button
            type="submit"
            block
            busy={busy}
            disabled={busy || digits(code).length < step.codeLength}
            data-testid="login-verify"
          >
            {t(lang, 'signIn')}
          </Button>
          {errorBanner}
          <div className="row-between">
            <button
              type="button"
              className="link-btn"
              onClick={() => setStep({ kind: 'email' })}
              disabled={busy}
            >
              {t(lang, 'otherAddress')}
            </button>
            <button
              type="button"
              className="link-btn"
              onClick={() => void sendCode(step.email)}
              disabled={busy}
            >
              {t(lang, 'resendCode')}
            </button>
          </div>
        </form>
      )}
    </main>
  );
}
